import type { Store } from "@inferno/renderer-shared-core";
import { inject } from "mobx-react";
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";

import {
  isPWSSessionBackend,
  PWSSessionState,
  PwsViewBillingInfoResponse,
  PwsViewUserResponse,
  RecurlyBillingInfo,
  ZypeSubscription,
} from "@inferno/renderer-shared-core";
import { Button, ButtonKind, FauxButton } from "../../../../../core/ui";
import Heading from "../../../../../core/ui/Heading.component";
import { AxiosLoaderResponse } from "../../../lib/coastCommon";
import { getUserSettingsLoaderCacheKey } from "../../../lib/coastUser";
import { CoastAlert, CoastAlertError } from "../common/coast-alert/CoastAlert.model";
import { useCoastAlerts } from "../common/coast-alert/CoastAlerts.hook";
import { CoastAlertsDisplay } from "../common/coast-alert/CoastAlertsDisplay.components";
import { CoastUserBillingInfoWrapper } from "./billing/CoastUserBillingInfoWrapper.component";
import "./CoastUserSettings.style.scss";
import { CoastUserBillingInfoDisplay } from "./CoastUserSettingsBillingInfoDisplay";
import { CoastUserSubscriptionsEditLoader } from "./subscriptions/CoastUserSubscriptionsEditLoader.component";
import { CoastUserSubscriptionsList } from "./subscriptions/CoastUserSubscriptionsList.component";
import { CoastUserUpdateLoginInfo } from "./user-login/CoastUserUpdateLoginInfo.component";

export type UserSettingBillingData = AxiosLoaderResponse<PwsViewBillingInfoResponse>;
export type UserSettingUserData = AxiosLoaderResponse<PwsViewUserResponse>;

interface CoastUserSettingsProps {
  billingData: UserSettingBillingData;
  userData: UserSettingUserData;
  store?: Store;
}

interface EditingSubscriptionState {
  subscription: ZypeSubscription;
  idx: number;
}

type SubMenu = "login" | "billingInfo" | null;
const subMenuKeys = new Set(["login", "billingInfo"]);

const isSubMenu = (v?: string | null): v is SubMenu => v === null || (!!v && subMenuKeys.has(v));

const CoastUserSettingsBase = ({ children }: PropsWithChildren<Record<string, unknown>>) => {
  return (
    <div className="component-coast-user-settings">
      <header className="header-logo-container">
        <div className="header-logo" />
      </header>
      {children}
    </div>
  );
};
export const CoastUserSettings = inject("store")(({ store, billingData, userData }: CoastUserSettingsProps) => {
  const [editingSubscription, setEditingSubscription] = useState<EditingSubscriptionState | null>();
  const [billingInfoError, setBillingInfoError] = useState<CoastAlert | null>(null);
  const { alerts, setErrorAlert, removeAlert, setMultipleAlerts } = useCoastAlerts();
  const [subMenu, setSubMenu] = useState<SubMenu>(null);
  const notLoggedInAlert = useRef<string | null>(null);
  const session = store?.session.currentSession;

  if (!store || !isPWSSessionBackend(session)) {
    return null;
  }

  const handleSessionUpdate = useCallback(
    (state: PWSSessionState) => {
      if (state.authenticated) {
        if (notLoggedInAlert.current) {
          removeAlert(notLoggedInAlert.current);
        }
      } else if (!notLoggedInAlert.current) {
        setErrorAlert("Seems like you're not logged it. Please login and try again");
      }
    },
    [removeAlert, setErrorAlert],
  );

  useEffect(() => {
    handleSessionUpdate(session.state);
    session.onStatusChanged.subscribe(handleSessionUpdate);
    return () => session.onStatusChanged.unsubscribe(handleSessionUpdate);
  }, [handleSessionUpdate, session.onStatusChanged, session.state]);

  useEffect(() => {
    const viewData = session.userModalState?.data;

    if (userData.error) {
      setErrorAlert(userData.error);
    }

    if (billingData.error) {
      setBillingInfoError(new CoastAlertError(billingData.error));
    }

    if (viewData && isSubMenu(viewData.subMenu)) {
      setSubMenu(viewData.subMenu);
    }
  }, [billingData.error, session.userModalState?.data, setErrorAlert, userData.error]);

  const handleNotificationsOnGoBack = useCallback(
    (notifications: CoastAlert[] = []) => {
      setMultipleAlerts(notifications);
    },
    [setMultipleAlerts],
  );

  const handleGoBack = useCallback(
    // notifications isn't really required here, but having it optional makes TS allow () => void definition
    // and that in its term makes it ok to pass this as onClick cb, which fails since that passes an event
    (notifications?: CoastAlert[]) => {
      setSubMenu(null);

      if (notifications?.length) {
        handleNotificationsOnGoBack(notifications);
      }
    },
    [handleNotificationsOnGoBack],
  );

  const handleSubscriptionEditBack = useCallback(
    // see above for why notifications is a required param
    (notifications?: CoastAlert[]) => {
      setEditingSubscription(null);
      handleNotificationsOnGoBack(notifications);
    },
    [handleNotificationsOnGoBack],
  );

  const updateSubscription = (updatedSubscription: ZypeSubscription, idxToUpdate: number) => {
    // this is stinky, but I don't know how to better do this than changing loader data by reference :facepalm
    // we need this so that if user updates the subscription through this component we can display it right away
    if (userData.data?.subscriptions) {
      userData.data.subscriptions[idxToUpdate] = updatedSubscription;

      store.storeCacheValue(getUserSettingsLoaderCacheKey(session.state.accessToken), {
        data: { userData, billingData },
      });
    }
  };

  const updateBillingInfo = (updatedBillingInfo: RecurlyBillingInfo) => {
    // this is stinky, but I don't know how to better do this than changing loader data by reference :facepalm
    // we need this so that if user updates their billing info through this component we can display it right away
    if (!billingData.data) {
      billingData.data = { billingInfo: updatedBillingInfo };
    } else {
      billingData.data.billingInfo = updatedBillingInfo;
    }

    store.storeCacheValue(getUserSettingsLoaderCacheKey(session.state.accessToken), {
      data: { userData, billingData },
    });
  };

  if (!userData.data) {
    return (
      <CoastUserSettingsBase>
        <Heading level={2}>My account</Heading>
        <CoastAlertsDisplay alerts={alerts} />
      </CoastUserSettingsBase>
    );
  }

  if (editingSubscription) {
    return (
      <CoastUserSettingsBase>
        <CoastUserSubscriptionsEditLoader
          subscription={editingSubscription.subscription}
          curSubscriptionIdx={editingSubscription.idx}
          billingInfo={billingData.data?.billingInfo}
          updateSubscription={updateSubscription}
          goBack={handleSubscriptionEditBack}
          billingInfoError={billingInfoError}
          setBillingInfo={updateBillingInfo}
          session={session}
        />
      </CoastUserSettingsBase>
    );
  }

  switch (subMenu) {
    case "login":
      return (
        <CoastUserSettingsBase>
          <CoastUserUpdateLoginInfo userData={userData.data} goBack={handleGoBack} session={session} />
        </CoastUserSettingsBase>
      );
    case "billingInfo":
      return (
        <CoastUserSettingsBase>
          <CoastUserBillingInfoWrapper
            billingInfo={billingData.data?.billingInfo}
            fetchingError={billingInfoError}
            onUpdate={updateBillingInfo}
            goBack={handleGoBack}
            session={session}
          />
        </CoastUserSettingsBase>
      );
  }

  return (
    <CoastUserSettingsBase>
      <Heading level={2}>My account</Heading>

      <CoastAlertsDisplay alerts={alerts} />

      <p className="current-account">
        You’re logged in as <strong>{userData.data.user.email}</strong>
        &nbsp;
        <Button className="button-logout" kind={ButtonKind.CUSTOMIZED} click={session.logout}>
          Log Out
        </Button>
      </p>

      <hr className="divider" />

      <section className="account-section">
        <div className="content">
          <Heading level={3}>Login</Heading>

          <p>
            <strong>Username: </strong>
            {userData.data.user.email}
          </p>
          <p>
            <strong>Password: </strong>**********
          </p>
          <p>
            <strong>Password Hint: </strong>
            {userData.data.user.password_hint || "Add a password hint to see it here"}
          </p>
        </div>

        <Button click={() => setSubMenu("login")} kind={ButtonKind.SECONDARY} className="account-button">
          Edit
        </Button>
      </section>

      <hr className="divider" />

      <CoastUserBillingInfoDisplay
        onEditClick={() => setSubMenu("billingInfo")}
        billingInfo={billingData.data?.billingInfo}
        fetchingError={billingInfoError}
        isSubMenu={true}
      />

      <hr className="divider" />

      <CoastUserSubscriptionsList
        onEdit={(subscription, idx) => setEditingSubscription({ subscription, idx })}
        subscriptions={userData.data.subscriptions}
      />

      <hr className="divider" />

      <section className="account-section">
        <div className="content">
          <Heading level={3}>Gift Coast Insider</Heading>

          <p>
            Give the gift of Coast Insider - the only way to listen to Coast to Coast AM on your computer or mobile
            device. All audio streams are delivered in high quality, crystal clear audio.
          </p>
        </div>

        <FauxButton to="/sign-up/?step=subscription&gift=true" kind={ButtonKind.SECONDARY} className="account-button">
          Give Gift
        </FauxButton>
      </section>
    </CoastUserSettingsBase>
  );
});
